import { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import { useGridApiContext } from '../lib/dataGrid';
import { downloadBlob } from '../utils/downloadFile';
import { useAuth } from '../features/auth/useAuth';
import ColumnPicker from './ColumnPicker';
import {
    arrangeColumns,
    reorderedEntries,
    withShown
} from './columnArrangement';
import { readCsvColumns, writeCsvColumns } from './csvColumnLayout';
import {
    csvColumnChoices,
    csvPickerTexts,
    csvRowIds,
    ExportLocationContext,
    gridCsvBlob,
    gridCsvFileName,
    type CsvRowScope
} from './gridCsvExport';

/**
 * Picks the columns and rows of a grid's CSV export, and the columns' order.
 * Opens with the columns the grid shows ticked, in the grid's order — or, for
 * a grid with a `gridId`, as the user last exported it (see csvColumnLayout).
 * Mount it only while open, so each opening starts from the grid as it is
 * then.
 */
export default function GridCsvExportDialog({
    exportName,
    gridId,
    onClose
}: {
    /** What the grid holds, when the page's name alone does not say. */
    exportName?: string;
    /** Remembers the user's columns for this grid; none without it. */
    gridId?: string;
    onClose: () => void;
}) {
    const apiRef = useGridApiContext();
    const location = useContext(ExportLocationContext);
    const userId = useAuth().user?.id;
    const storeFor = userId && gridId ? { userId, gridId } : null;
    const [columns] = useState(() => csvColumnChoices(apiRef));
    const [defaultColumns] = useState(() =>
        columns.map((column) => ({
            key: column.field,
            preset: column.visible
        }))
    );
    const [arranged, setArranged] = useState(() =>
        arrangeColumns(
            defaultColumns,
            storeFor ? readCsvColumns(storeFor.userId, storeFor.gridId) : []
        )
    );
    const labelOf = new Map(
        columns.map((column) => [column.field, column.label])
    );
    const choices = arranged.map((entry) => ({
        field: entry.key,
        label: labelOf.get(entry.key) ?? entry.key,
        visible: columns.some(
            (column) => column.field === entry.key && column.visible
        )
    }));
    const selectedFields = new Set(
        arranged.filter((entry) => entry.shown).map((entry) => entry.key)
    );
    const [rowCounts] = useState(() => ({
        filtered: csvRowIds(apiRef, 'filtered').length,
        all: csvRowIds(apiRef, 'all').length
    }));
    const [scope, setScope] = useState<CsvRowScope>('filtered');
    const [pickerTexts] = useState(() => csvPickerTexts(apiRef));

    const noneSelected = selectedFields.size === 0;

    function exportCsv() {
        // In the order of the list, not in the order they were ticked.
        const fields = arranged
            .filter((entry) => entry.shown)
            .map((entry) => entry.key);
        downloadBlob(
            gridCsvBlob(apiRef, fields, scope),
            gridCsvFileName(location, exportName)
        );
        if (storeFor) {
            writeCsvColumns(storeFor.userId, storeFor.gridId, arranged);
        }
        onClose();
    }

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <FormattedMessage id="grid.csvExport.title" />
            </DialogTitle>
            <DialogContent>
                <FormLabel component="legend">
                    <FormattedMessage id="grid.csvExport.rows" />
                </FormLabel>
                <RadioGroup
                    value={scope}
                    onChange={(event) =>
                        setScope(event.target.value as CsvRowScope)
                    }
                >
                    <FormControlLabel
                        value="filtered"
                        control={<Radio size="small" />}
                        label={
                            <FormattedMessage
                                id="grid.csvExport.rows.filtered"
                                values={{ count: rowCounts.filtered }}
                            />
                        }
                    />
                    <FormControlLabel
                        value="all"
                        control={<Radio size="small" />}
                        label={
                            <FormattedMessage
                                id="grid.csvExport.rows.all"
                                values={{ count: rowCounts.all }}
                            />
                        }
                    />
                </RadioGroup>
                <Divider sx={{ my: 1.5 }} />
                <FormLabel component="legend" sx={{ mb: 1 }}>
                    <FormattedMessage id="grid.csvExport.columns" />
                </FormLabel>
                <ColumnPicker
                    columns={choices}
                    selectedFields={selectedFields}
                    onChange={(fields) =>
                        setArranged((current) => withShown(current, fields))
                    }
                    onReorder={(fields) =>
                        setArranged((current) =>
                            reorderedEntries(current, fields)
                        )
                    }
                    onReset={() =>
                        setArranged(arrangeColumns(defaultColumns, []))
                    }
                    texts={pickerTexts}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    <FormattedMessage id="common.cancel" />
                </Button>
                <Button
                    variant="contained"
                    onClick={exportCsv}
                    disabled={noneSelected}
                >
                    <FormattedMessage id="grid.csvExport.submit" />
                </Button>
            </DialogActions>
        </Dialog>
    );
}
